import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { keyframes } from "@emotion/react";
import { alpha, useTheme } from "@mui/material/styles";

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const reducedMotion = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

// Shimmering placeholder block
export function ShimmerBlock({ sx }) {
  const theme = useTheme();
  const base = alpha(theme.palette.primary.main, 0.08);
  const highlight = alpha(theme.palette.primary.main, 0.18);
  return (
    <Box
      sx={{
        borderRadius: 1.5,
        background: `linear-gradient(90deg, ${base} 25%, ${highlight} 50%, ${base} 75%)`,
        backgroundSize: "200% 100%",
        animation: `${shimmer} 1.6s ease-in-out infinite`,
        ...reducedMotion,
        ...sx,
      }}
    />
  );
}

// Glassy chart card wrapping the animated chart placeholder
function ChartCardSkeleton({ height }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        height,
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
        bgcolor: alpha("#ffffff", 0.7),
        backdropFilter: "blur(12px)",
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        boxShadow: `0 20px 50px ${alpha(theme.palette.primary.dark, 0.12)}`,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 3, pb: 0 }}
      >
        <ShimmerBlock sx={{ height: 20, width: "40%" }} />
        <Box sx={{ flex: 1 }} />
        <ShimmerBlock sx={{ height: 28, width: 28, borderRadius: "50%" }} />
      </Box>
      <Box
        role="status"
        aria-label="Loading chart"
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={24} />
      </Box>
    </Box>
  );
}

// Skeleton matching the hero slide layout while dashboards are fetched
export function HeroSkeleton({ mode = "home" }) {
  const isHome = mode === "home";
  const height = isHome ? "max(75vh,15cm)" : "max(85vh,17cm)";

  return (
    <Grid
      p={1}
      container
      spacing={4}
      alignItems="center"
      sx={{ animation: `${fadeUp} 0.5s ease-out both` }}
    >
      {isHome && (
        <Grid item xs={12} md={4}>
          <ShimmerBlock sx={{ height: 28, width: "85%", mb: 1.5 }} />
          <ShimmerBlock sx={{ height: 28, width: "60%", mb: 3 }} />
          <ShimmerBlock sx={{ height: 16, width: "100%", mb: 1.25 }} />
          <ShimmerBlock sx={{ height: 16, width: "92%", mb: 1.25 }} />
          <ShimmerBlock sx={{ height: 16, width: "75%", mb: 4 }} />
          <ShimmerBlock sx={{ height: 44, width: 210, borderRadius: 2 }} />
        </Grid>
      )}
      <Grid item xs={12} md={isHome ? 8 : 12}>
        <ChartCardSkeleton height={height} />
      </Grid>
    </Grid>
  );
}

export default HeroSkeleton;
